import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FaIndustry, FaRocket, FaCheck, FaCogs, FaShieldAlt,
  FaMicrochip, FaLayerGroup, FaCheckCircle, FaArrowRight,
  FaCar, FaHeartbeat, FaTrain, FaPlane, FaBolt,
  FaChevronLeft, FaChevronRight, FaMobile, FaLeaf,
  FaBoxOpen, FaMapMarkerAlt,
  FaCertificate, FaTools, FaChartLine, FaPlay, FaPause,
  FaStar, FaQuoteLeft
} from 'react-icons/fa';
import logo from '../assets/logo.png';
import membershipPdf from '../pdf/A-269 (Start-up) SaalfoTech.pdf';

/* ─── Animated Counter ─── */
function Counter({ end, suffix }) {
  const [count, setCount] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      let start = 0;
      const inc = end / (1800 / 16);
      const t = setInterval(() => {
        start += inc;
        if (start >= end) { setCount(end); clearInterval(t); }
        else setCount(Math.floor(start));
      }, 16);
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end]);
  return <span ref={ref}>{count.toLocaleString()}{suffix || ''}</span>;
}

/* ─── Hero Image Slider ─── */
function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const slides = [
    { img: 'https://images.pexels.com/photos/163170/board-printed-circuit-board-computer-electronics-163170.jpeg', label: 'PCB Assembly' },
    { img: 'https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg', label: 'SMT Production' },
    { img: 'https://images.pexels.com/photos/5474295/pexels-photo-5474295.jpeg', label: 'Quality Testing' },
    { img: 'https://images.pexels.com/photos/3912981/pexels-photo-3912981.jpeg', label: 'Electronics Manufacturing' },
    { img: 'https://images.pexels.com/photos/6755078/pexels-photo-6755078.jpeg', label: 'Box Build Assembly' },
  ];
  useEffect(() => {
    const t = setInterval(() => setCurrent(p => (p + 1) % slides.length), 4000);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
      {slides.map((s, i) => (
        <div key={i} style={{
          position: 'absolute', inset: 0,
          opacity: i === current ? 1 : 0,
          transition: 'opacity 1.2s ease',
        }}>
          <img src={s.img} alt={s.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,30,10,0.88) 0%, rgba(10,25,30,0.75) 50%, rgba(5,15,10,0.65) 100%)' }} />
        </div>
      ))}
      {/* Slide indicators */}
      <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, zIndex: 5 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={() => setCurrent(i)} style={{
            width: i === current ? 28 : 8, height: 8,
            borderRadius: 4, border: 'none', cursor: 'pointer',
            background: i === current ? '#5cb85c' : 'rgba(255,255,255,0.4)',
            transition: 'all 0.3s', padding: 0,
          }} />
        ))}
      </div>
      {/* Current label */}
      <div style={{ position: 'absolute', bottom: 28, right: 32, zIndex: 5, padding: '4px 14px', background: 'rgba(92,184,92,0.2)', border: '1px solid rgba(92,184,92,0.4)', borderRadius: 100, fontSize: 11, color: 'rgba(255,255,255,0.8)', fontWeight: 600, backdropFilter: 'blur(8px)' }}>
        {slides[current].label}
      </div>
    </div>
  );
}

/* ─── Auto Image Slider (for service cards) ─── */
function AutoSlider({ images, interval = 3000 }) {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCurrent(p => (p + 1) % images.length), interval);
    return () => clearInterval(t);
  }, [images.length, interval]);
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      {images.map((img, i) => (
        <div key={i} style={{
          position: 'absolute', inset: 0,
          opacity: i === current ? 1 : 0,
          transition: 'opacity 0.9s ease',
        }}>
          <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      ))}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 60%)' }} />
      <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 5 }}>
        {images.map((_, i) => (
          <div key={i} style={{ width: i === current ? 18 : 5, height: 5, borderRadius: 3, background: i === current ? '#5cb85c' : 'rgba(255,255,255,0.5)', transition: 'all 0.3s' }} />
        ))}
      </div>
    </div>
  );
}

/* ─── Industry Slider ─── */
function IndustrySlider({ industry }) {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef();
  const startAuto = () => {
    timerRef.current = setInterval(() => setCurrent(p => (p + 1) % industry.images.length), 3500);
  };
  useEffect(() => { startAuto(); return () => clearInterval(timerRef.current); }, []);
  const go = dir => {
    clearInterval(timerRef.current);
    setCurrent(p => (p + dir + industry.images.length) % industry.images.length);
    startAuto();
  };
  return (
    <div style={{ position: 'relative', borderRadius: 20, overflow: 'hidden', aspectRatio: '16/9', background: '#e8f5e8', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
      {industry.images.map((img, i) => (
        <div key={i} style={{ position: 'absolute', inset: 0, opacity: i === current ? 1 : 0, transition: 'opacity 0.8s ease' }}>
          <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      ))}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,0.5) 0%,transparent 55%)' }} />
      <button onClick={() => go(-1)} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', zIndex: 2, background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '50%', width: 38, height: 38, color: '#2d6a2d', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', transition: 'all 0.2s' }}>
        <FaChevronLeft size={13} />
      </button>
      <button onClick={() => go(1)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', zIndex: 2, background: 'rgba(255,255,255,0.95)', border: 'none', borderRadius: '50%', width: 38, height: 38, color: '#2d6a2d', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', transition: 'all 0.2s' }}>
        <FaChevronRight size={13} />
      </button>
      <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6, zIndex: 2 }}>
        {industry.images.map((_, i) => (
          <button key={i} onClick={() => { clearInterval(timerRef.current); setCurrent(i); startAuto(); }} style={{ width: i === current ? 24 : 8, height: 8, borderRadius: 4, border: 'none', cursor: 'pointer', background: i === current ? '#5cb85c' : 'rgba(255,255,255,0.6)', transition: 'all 0.3s', padding: 0 }} />
        ))}
      </div>
    </div>
  );
}

/* ─── Equipment Card ─── */
function EquipmentCard({ title, brand, brandColor, brandBg, specs, badge, accentColor = '#5cb85c', delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(() => setVisible(true), delay); obs.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className="equip-card" style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.6s cubic-bezier(0.22,1,0.36,1)', '--ac': accentColor }}>
      <div className="equip-card-header" style={{ background: brandBg || `linear-gradient(135deg,${accentColor}15,${accentColor}06)` }}>
        <span className="equip-badge-top" style={{ background: `linear-gradient(135deg,${accentColor},${accentColor}bb)` }}>{badge}</span>
        <div className="equip-brand" style={{ color: brandColor }}>{brand}</div>
      </div>
      <div className="equip-body">
        <div className="equip-title">{title}</div>
        <div className="equip-specs">
          {specs.map((s, i) => (
            <div key={i} className="equip-spec-row" style={{ background: i % 2 === 0 ? `${accentColor}06` : 'transparent' }}>
              <span className="equip-spec-label">{s.label}</span>
              <span className="equip-spec-val" style={{ color: accentColor }}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="equip-glow" style={{ background: `radial-gradient(circle,${accentColor}15,transparent 70%)` }} />
    </div>
  );
}

/* ─── Data ─── */
const heroStats = [
  { end: 35, suffix: '+', label: 'Yrs Group Exp.' },
  { end: 20, suffix: 'M+', label: 'Meters Produced' },
  { end: 100, suffix: '+', label: 'Employees' },
  { end: 172, suffix: 'K', label: 'Sq.Ft. Facility' },
];

const services = [
  {
    icon: <FaLayerGroup />, title: 'EMS Solutions',
    desc: 'Complete turnkey manufacturing — component sourcing, PCBA, testing, and global logistics.',
    to: '/ems-solutions', color: '#0d6e6e', bg: 'linear-gradient(135deg,#f0fdfa,#ccfbf1)',
    images: [
      'https://images.pexels.com/photos/11288389/pexels-photo-11288389.jpeg',
      'https://images.pexels.com/photos/8442025/pexels-photo-8442025.jpeg',
      'https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg',
    ]
  },
  {
    icon: <FaCogs />, title: 'Manufacturing',
    desc: 'PCBA, cable harness, box build, and full product integration with IPC-certified processes.',
    to: '/manufacturing', color: '#1e5c9e', bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
    images: [
      'https://images.pexels.com/photos/5554949/pexels-photo-5554949.jpeg',
      'https://images.pexels.com/photos/37426135/pexels-photo-37426135.jpeg',
      'https://images.pexels.com/photos/9242888/pexels-photo-9242888.jpeg',
    ]
  },
  {
    icon: <FaBoxOpen />, title: 'Box Build',
    desc: 'End-to-end product assembly with EOL testing, display integration, and full product validation.',
    to: '/manufacturing', color: '#7c3d8a', bg: 'linear-gradient(135deg,#faf5ff,#ede9fe)',
    images: [
      'https://images.pexels.com/photos/17937669/pexels-photo-17937669.jpeg',
      'https://images.pexels.com/photos/11765539/pexels-photo-11765539.jpeg',
      'https://images.pexels.com/photos/28752150/pexels-photo-28752150.jpeg',
    ]
  },
];

const perfStats = [
  { value: '125K', label: 'SMT CPH Throughput', sub: '4-sigma accuracy', icon: '⚡' },
  { value: '96–99%', label: 'On-Time Delivery', sub: 'Across all segments', icon: '🎯' },
  { value: '<750', label: 'PPM Defect Rate', sub: 'Average monthly quality', icon: '✅' },
  { value: 'Class III', label: 'IPC Certified', sub: 'Mission-critical ready', icon: '🏅' },
];

const whyUs = [
  { icon: <FaShieldAlt />, title: 'ISO 9001:2015 Certified', desc: 'TUV SUD certified. IPC Class II & III approved facility. Full production traceability via PanaCIM. ZED Bronze by Ministry of MSME.', color: '#2e7d32', bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', img: 'https://images.pexels.com/photos/8177924/pexels-photo-8177924.jpeg' },
  { icon: <FaRocket />, title: 'Speed & Scale', desc: 'From 10-unit NPI builds to 100K+ series production. 125K CPH SMT throughput. Expandable up to 80,000 Sq.Ft. production floor.', color: '#0d6e6e', bg: 'linear-gradient(135deg,#f0fdfa,#ccfbf1)', img: 'https://images.pexels.com/photos/3691336/pexels-photo-3691336.jpeg' },
  { icon: <FaCheckCircle />, title: 'End-to-End Partner', desc: 'Bare PCB → Components → PCBA → Cable Harness → Box Build — all under one roof in Mysore, India.', color: '#1e5c9e', bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)', img: 'https://images.pexels.com/photos/8428059/pexels-photo-8428059.jpeg' },
  { icon: <FaChartLine />, title: 'Proven Track Record', desc: 'Over 20 million smart meters produced. Trusted by global OEMs from Japan, Europe, and the USA.', color: '#b45309', bg: 'linear-gradient(135deg,#fffbeb,#fef3c7)', img: 'https://images.pexels.com/photos/28403060/pexels-photo-28403060.jpeg' },
  { icon: <FaTools />, title: 'In-House Jigs & Fixtures', desc: 'Custom test jigs, conformal coating, gluing, UL-compliant assembly processes, and COC traceability.', color: '#7c3d8a', bg: 'linear-gradient(135deg,#faf5ff,#ede9fe)', img: 'https://images.pexels.com/photos/14805032/pexels-photo-14805032.jpeg' },
  { icon: <FaCertificate />, title: 'Multi-Standard Ready', desc: 'Targeting ISO 14001, ISO 45001, ISO 13485, and AS 9100 certifications in 2026-27 for expanded sectors.', color: '#c2410c', bg: 'linear-gradient(135deg,#fff7ed,#fed7aa)', img: 'https://images.pexels.com/photos/35573433/pexels-photo-35573433.jpeg' },
];

// const keyProjects = [
//   { sector: 'Industrial', company: 'Milton Roy (USA)', scope: 'Box Build, PCBA & Wiring Harness', metric: '10,000', metricLabel: 'PCBA Produced', otd: '96%', ppm: '<250 PPM', color: '#2e7d32', bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)' },
//   { sector: 'Smart Energy', company: 'IOT Smart Meters', scope: 'PCBA, Harness, Box Build & EOL Testing', metric: '20M+', metricLabel: 'Meters Produced', otd: '96.7%', ppm: '<750 PPM', color: '#0d6e6e', bg: 'linear-gradient(135deg,#f0fdfa,#ccfbf1)' },
//   { sector: 'Railways', company: 'Japanese OEM', scope: 'PCBA & Wiring Harness', metric: '3,000', metricLabel: 'PCBA Produced', otd: '98%', ppm: '<500 PPM', color: '#1e5c9e', bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)' },
//   { sector: 'Railways', company: 'European OEM', scope: 'PCBA, Harness & Box Build', metric: '4,900', metricLabel: 'Monthly Qty', otd: '99%', ppm: '<500 PPM', color: '#1565c0', bg: 'linear-gradient(135deg,#e8f0fe,#c7d7f8)' },
//   { sector: 'Defence', company: 'Drone Power Systems', scope: 'DFX, NPI, PCBA & Wiring Harness', metric: 'NPI→', metricLabel: 'Production Ready', otd: '100%', ppm: 'Class III', color: '#7c3d8a', bg: 'linear-gradient(135deg,#faf5ff,#ede9fe)' },
//   { sector: 'Automotive', company: 'Regulator Systems', scope: 'DFX, NPI & PCBA', metric: 'DFX→', metricLabel: 'NPI Complete', otd: '100%', ppm: 'IPC III', color: '#c2410c', bg: 'linear-gradient(135deg,#fff7ed,#fed7aa)' },
// ];
const keyProjects = [
  {
    sector: 'Industrial',
    company: 'USA OEM',
    scope: 'Box Build, PCBA & Wiring Harness',
    metric: '10,000',
    metricLabel: 'PCBA Produced',
    otd: '96%',
    color: '#2e7d32',
    bg: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
  },
  {
    sector: 'Smart Energy',
    company: 'IOT Smart Meters',
    scope: 'PCBA, Harness, Box Build & EOL Testing',
    metric: '20M+',
    metricLabel: 'Meters Produced',
    otd: '96.7%',
    color: '#0d6e6e',
    bg: 'linear-gradient(135deg,#f0fdfa,#ccfbf1)',
  },
  {
    sector: 'Railways',
    company: 'Japanese OEM',
    scope: 'PCBA & Wiring Harness',
    metric: '3,000',
    metricLabel: 'PCBA Produced',
    otd: '98%',
    color: '#1e5c9e',
    bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
  },
  {
    sector: 'Railways',
    company: 'European OEM',
    scope: 'PCBA, Harness & Box Build',
    metric: '4,900',
    metricLabel: 'Monthly Qty',
    otd: '99%',
    color: '#1565c0',
    bg: 'linear-gradient(135deg,#e8f0fe,#c7d7f8)',
  },
  {
    sector: 'Defence',
    company: 'Drone Power Systems',
    scope: 'DFX, NPI, PCBA & Wiring Harness',
    metric: 'NPI→',
    metricLabel: 'Production Ready',
    otd: '100%',
    color: '#7c3d8a',
    bg: 'linear-gradient(135deg,#faf5ff,#ede9fe)',
  },
  {
    sector: 'Automotive',
    company: 'Regulator Systems',
    scope: 'DFX, NPI & PCBA',
    metric: 'DFX→',
    metricLabel: 'NPI Complete',
    otd: '100%',
    color: '#c2410c',
    bg: 'linear-gradient(135deg,#fff7ed,#fed7aa)',
  },
];
const industrySliders = [
  { id: 'automotive', name: 'Automotive', icon: <FaCar />, desc: 'ECUs, ADAS, BMS, body control units and infotainment systems built to AEC-Q100 standards.', color: '#1e5c9e', images: ['https://images.pexels.com/photos/163170/board-printed-circuit-board-computer-electronics-163170.jpeg', 'https://images.pexels.com/photos/6755078/pexels-photo-6755078.jpeg', 'https://images.pexels.com/photos/6755059/pexels-photo-6755059.jpeg', 'https://images.pexels.com/photos/28136812/pexels-photo-28136812.jpeg', 'https://images.pexels.com/photos/6755051/pexels-photo-6755051.jpeg'] },
  { id: 'industrial', name: 'Industrial', icon: <FaIndustry />, desc: 'PLCs, HMI panels, motor drives, smart meters and Industry 4.0 sensor nodes.', color: '#2e7d32', images: ['https://images.pexels.com/photos/5554948/pexels-photo-5554948.jpeg', 'https://images.pexels.com/photos/5554949/pexels-photo-5554949.jpeg', 'https://images.pexels.com/photos/16647824/pexels-photo-16647824.jpeg', 'https://images.pexels.com/photos/3912981/pexels-photo-3912981.jpeg', 'https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg'] },
  { id: 'healthcare', name: 'Healthcare / Medical', icon: <FaHeartbeat />, desc: 'Patient monitoring, diagnostic, and surgical instruments meeting ISO 13485 requirements.', color: '#c62828', images: ['https://images.pexels.com/photos/13176358/pexels-photo-13176358.jpeg', 'https://images.pexels.com/photos/11288658/pexels-photo-11288658.jpeg', 'https://images.pexels.com/photos/7617612/pexels-photo-7617612.jpeg', 'https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg', 'https://images.pexels.com/photos/3862130/pexels-photo-3862130.jpeg'] },
  { id: 'railways', name: 'Railways', icon: <FaTrain />, desc: 'Trackside electronics, axle counting, signalling boards, and passenger info systems for EN 50155.', color: '#1565c0', images: ['https://images.pexels.com/photos/7668267/pexels-photo-7668267.jpeg', 'https://images.pexels.com/photos/16647824/pexels-photo-16647824.jpeg', 'https://images.pexels.com/photos/5474295/pexels-photo-5474295.jpeg', 'https://images.pexels.com/photos/6755078/pexels-photo-6755078.jpeg', 'https://images.pexels.com/photos/163170/board-printed-circuit-board-computer-electronics-163170.jpeg'] },
  { id: 'ev', name: 'Electric Vehicles (EV)', icon: <FaBolt />, desc: 'BMS boards, onboard chargers, DC-DC converters, and powertrain control electronics.', color: '#2e7d32', images: ['https://images.pexels.com/photos/12860663/pexels-photo-12860663.jpeg', 'https://images.pexels.com/photos/999290/pexels-photo-999290.jpeg', 'https://images.pexels.com/photos/6755074/pexels-photo-6755074.jpeg', 'https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg', 'https://images.pexels.com/photos/3912981/pexels-photo-3912981.jpeg'] },
  { id: 'defence', name: 'Defence & Aerospace', icon: <FaPlane />, desc: 'High-reliability IPC Class III assemblies for drones, avionics, and mission-critical defence systems.', color: '#7c3d8a', images: ['https://images.pexels.com/photos/6896138/pexels-photo-6896138.jpeg', 'https://images.pexels.com/photos/13708420/pexels-photo-13708420.jpeg', 'https://images.pexels.com/photos/5474295/pexels-photo-5474295.jpeg', 'https://images.pexels.com/photos/6755078/pexels-photo-6755078.jpeg', 'https://images.pexels.com/photos/163170/board-printed-circuit-board-computer-electronics-163170.jpeg'] },
  { id: 'consumer', name: 'Consumer Electronics', icon: <FaMobile />, desc: 'Smart home devices, IoT wearables, and connected products with fast NPI and scalable production.', color: '#b45309', images: ['https://images.pexels.com/photos/2588756/pexels-photo-2588756.jpeg', 'https://images.pexels.com/photos/31945005/pexels-photo-31945005.jpeg', 'https://images.pexels.com/photos/5554949/pexels-photo-5554949.jpeg', 'https://images.pexels.com/photos/6755078/pexels-photo-6755078.jpeg', 'https://images.pexels.com/photos/6755059/pexels-photo-6755059.jpeg'] },
];

/* ─── Main ─── */
export default function Home() {
  const [activeIndustry, setActiveIndustry] = useState(0);

  return (
    <div style={{ fontFamily: "'DM Sans',sans-serif", overflowX: 'hidden' }}>
      <style>{globalStyles}</style>

      {/* ══ HERO — Full Background Image Slider ══ */}
      <section className="hero-section" style={{ paddingTop: 0, minHeight: '100vh', position: 'relative', display: 'flex', alignItems: 'center' }}>
        <HeroSlider />

        {/* Decorative animated ring */}
        <div style={{ position: 'absolute', top: '10%', right: '5%', width: 360, height: 360, borderRadius: '50%', border: '1.5px dashed rgba(92,184,92,0.25)', animation: 'rotateSlow 25s linear infinite', zIndex: 1, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '15%', left: '3%', width: 220, height: 220, borderRadius: '50%', border: '1px solid rgba(92,184,92,0.15)', animation: 'rotateSlow 18s linear infinite reverse', zIndex: 1, pointerEvents: 'none' }} />

        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 40px', position: 'relative', zIndex: 2, paddingTop: 120 }}>
          <div className="hero-grid">
            {/* LEFT CONTENT */}
            <div className="hero-left fade-up">
              <div className="hero-badge" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', border: '1px solid rgba(92,184,92,0.5)', color: '#a8f0a8' }}>
                <FaMapMarkerAlt size={10} style={{ color: '#5cb85c' }} />
                ISO 9001:2015 Certified · Mysore, India · Est. 2016
              </div>

              <h1 className="hero-h1" style={{ color: '#fff' }}>
                Safer, Smarter &<br />
                <span className="glow-text">Sustainable</span><br />
                <span style={{ color: 'rgba(255,255,255,0.9)' }}>Electronics</span>
              </h1>

              <p className="hero-sub" style={{ color: 'rgba(255,255,255,0.75)' }}>
                Mysore's fastest-growing EMS company — delivering complete electronics manufacturing
                from bare PCB to fully tested box build. Proudly supporting <strong style={{ color: '#7ddc7d' }}>Make in India</strong>.
              </p>

              <div className="hero-btns">
                <Link to="/ems-solutions" className="btn-glow">
                  Explore EMS Services <FaArrowRight size={12} />
                </Link>
                <Link to="/projects" className="btn-ghost" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', border: '1.5px solid rgba(255,255,255,0.3)', color: '#fff' }}>
                  View Projects
                </Link>
              </div>

              <div className="cert-pills">
                {['🏅 ISO 9001:2015', '🏆 ZED Bronze', '✅ IPC Class III', '🇮🇳 Make in India'].map(c => (
                  <span key={c} className="cert-pill" style={{ background: 'rgba(92,184,92,0.15)', border: '1px solid rgba(92,184,92,0.4)', color: '#a8f0a8', backdropFilter: 'blur(6px)' }}>{c}</span>
                ))}
              </div>
            </div>

            {/* RIGHT CONTENT — Stats Card */}
            <div className="hero-right fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="hero-card" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', border: '1.5px solid rgba(255,255,255,0.2)', boxShadow: '0 32px 80px rgba(0,0,0,0.4)' }}>
                <div className="hero-card-glow" />
                <div className="hero-card-accent" />
{/* 
                <img src={logo} alt="Saalfo Tech" className="hero-logo" style={{ filter: 'brightness(0) invert(1)', marginBottom: 8 }} /> */}
                <p className="hero-card-tag" style={{ color: '#7ddc7d' }}>SAALFO TECH PVT LTD</p>

                <div className="stat-grid">
                  {heroStats.map(s => (
                    <div key={s.label} className="stat-box" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
                      <div className="stat-num" style={{ color: '#7ddc7d' }}>
                        <Counter end={s.end} suffix={s.suffix} />
                      </div>
                      <div className="stat-label" style={{ color: 'rgba(255,255,255,0.7)' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                <div className="hero-pills">
                  <span className="hero-pill" style={{ background: 'rgba(92,184,92,0.2)', border: '1px solid rgba(92,184,92,0.4)', color: '#a8f0a8' }}>172K Sq.Ft Facility</span>
                  <span className="hero-pill" style={{ background: 'rgba(92,184,92,0.2)', border: '1px solid rgba(92,184,92,0.4)', color: '#a8f0a8' }}>40K Production Floor</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 120, background: 'linear-gradient(to bottom, transparent, #fff)', zIndex: 3 }} />
      </section>

      {/* ══ MARQUEE ══ */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          {[...Array(2)].map((_, rep) =>
            ['PCB Assembly', 'Cable Harness', 'Box Build', 'SMT Line', 'BGA Rework', 'AOI Inspection', 'SPI Inspection', 'Conformal Coating', 'EOL Testing', 'NPI Build', 'Design Services', 'IPC Class III'].map((t, i) => (
              <span key={`${rep}-${i}`} className="marquee-item"><FaCheck size={9} color="#5cb85c" /> {t}</span>
            ))
          )}
        </div>
      </div>

      {/* ══ SERVICES — Image Slider Cards ══ */}
      <section className="sec-pad" style={{ background: 'linear-gradient(160deg, #f8fff8, #fff, #f0fdfa)', position: 'relative', overflow: 'hidden' }}>
        {/* BG pattern */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(circle, rgba(92,184,92,0.04) 1px, transparent 1px)', backgroundSize: '40px 40px', pointerEvents: 'none' }} />

        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 40px', position: 'relative', zIndex: 1 }}>
          <div className="sec-header fade-up">
            <span className="chip">What We Do</span>
            <h2 className="sec-h2">Complete EMS <span className="green">Capabilities</span></h2>
            <p className="sec-sub">From first prototype to full-scale production — every stage of your product lifecycle.</p>
            <div className="hline" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 28 }}>
            {services.map((s, i) => (
              <Link key={s.title} to={s.to} style={{ textDecoration: 'none' }} className="img-svc-card">
                {/* Image area */}
                <div style={{ height: 220, borderRadius: '18px 18px 0 0', overflow: 'hidden', position: 'relative' }}>
                  <AutoSlider images={s.images} interval={3200 + i * 500} />
                  <div style={{ position: 'absolute', top: 16, left: 16, zIndex: 3, width: 44, height: 44, borderRadius: 12, background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 18, boxShadow: `0 6px 20px ${s.color}55` }}>
                    {s.icon}
                  </div>
                </div>
                {/* Text area */}
                <div style={{ padding: '22px 24px 26px', background: '#fff', borderRadius: '0 0 18px 18px', border: `1.5px solid ${s.color}25`, borderTop: 'none' }}>
                  <h3 style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 10 }}>{s.title}</h3>
                  <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.65, marginBottom: 14 }}>{s.desc}</p>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: s.color }}>
                    Learn more <FaArrowRight size={10} />
                  </span>
                  <div style={{ width: '100%', height: 3, background: `linear-gradient(90deg, ${s.color}, ${s.color}66)`, borderRadius: 3, marginTop: 14 }} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PERF STATS ══ */}
      <section className="stats-section">
        <div className="stats-shimmer" />
        {/* Background image overlay */}
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.pexels.com/photos/2582937/pexels-photo-2582937.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.08 }} />
        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 40px', position: 'relative', zIndex: 1 }}>
          <div className="stats-row">
            {perfStats.map(s => (
              <div key={s.label} className="perf-card">
                <div className="perf-icon">{s.icon}</div>
                <div className="perf-val">{s.value}</div>
                <div className="perf-label">{s.label}</div>
                <div className="perf-sub">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ ABOUT — with facility image ══ */}
     <section
  className="sec-pad about-section"
  style={{
    position: 'relative',
    overflow: 'hidden',
  }}
>
  <div
    style={{
      position: 'absolute',
      inset: 0,
      background:
        'linear-gradient(160deg,#f0fdf4,#e8f5e8 30%,#f0fdfa 65%,#eff6ff 100%)',
    }}
  />

  <div
    className="wrap"
    style={{
      width: '100%',
      maxWidth: '100%',
      padding: '0 40px',
      position: 'relative',
      zIndex: 1,
    }}
  >
    <div className="sec-header fade-up">
      <span className="chip">About Saalfo Tech</span>

      <h2 className="sec-h2">
        Operational <span className="green">Intelligence</span>
      </h2>

      <div className="hline" />
    </div>

    {/* Responsive Layout */}
    <div
      className="about-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 40,
        marginBottom: 40,
        alignItems: 'center',
      }}
    >
      {/* CONTENT */}
      <div className="fade-up about-content">
        <div className="bento-tags">
          {[
            ['EST. 2016', 'green'],
            ['MYSORE, INDIA', 'teal'],
            ['35+ YRS GROUP EXP.', 'amber'],
          ].map(([t, c]) => (
            <span key={t} className={`btag btag-${c}`}>
              {t}
            </span>
          ))}
        </div>

        <p className="bento-body" style={{ marginBottom: 16 }}>
          Saalfo Tech is part of a conglomerate with over{' '}
          <strong>35 years of experience</strong> across Electronics
          Manufacturing, Education, and Textile. Our EMS domain expertise spans{' '}
          <strong>20+ years</strong>, scaling seamlessly from{' '}
          <strong>10 units to 100,000+</strong> with Class II & III
          reliability.
        </p>

        <p className="bento-body" style={{ marginBottom: 24 }}>
          Our long-term strategy supports <strong>Make in India</strong> — with
          in-house bare PCB and critical component design, we deliver true
          end-to-end EMS solutions: Bare PCB → Components → PCBA → Cable Harness
          → Box Build.
        </p>

        <div className="mini-stats">
          {[
            ['100+', 'Specialists', '#2e7d32', '#f0fdf4'],
            ['20+', 'Yrs EMS Exp.', '#0d6e6e', '#f0fdfa'],
            ['ISO', '9001:2015', '#1e5c9e', '#eff6ff'],
            ['ZED', 'Bronze', '#b45309', '#fffbeb'],
          ].map(([v, l, c, bg]) => (
            <div
              key={l}
              className="mini-stat"
              style={{
                '--mc': c,
                '--mbg': bg,
              }}
            >
              <div className="mini-val">{v}</div>
              <div className="mini-label">{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* IMAGE */}
      <div
        className="fade-up about-image"
        style={{
          position: 'relative',
          borderRadius: 24,
          overflow: 'hidden',
          height: 420,
          boxShadow: '0 24px 70px rgba(0,0,0,0.18)',
        }}
      >
        <AutoSlider
          images={[
            'https://images.pexels.com/photos/32529341/pexels-photo-32529341.jpeg',
            'https://images.pexels.com/photos/32845663/pexels-photo-32845663.jpeg',
            'https://images.pexels.com/photos/35638979/pexels-photo-35638979.jpeg',
            'https://images.pexels.com/photos/29828496/pexels-photo-29828496.jpeg',
          ]}
          interval={3800}
        />

        {/* Overlay Cards */}
        <div
          style={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            right: 20,
            zIndex: 4,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
          }}
        >
          {[
            ['172,000', 'Sq.Ft. Facility'],
            ['40K', 'Production Floor'],
          ].map(([v, l]) => (
            <div
              key={l}
              style={{
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(14px)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: 14,
                padding: '14px 16px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  fontFamily: "'Rajdhani',sans-serif",
                  fontSize: 26,
                  fontWeight: 700,
                  color: '#7ddc7d',
                  lineHeight: 1,
                }}
              >
                {v}
              </div>

              <div
                style={{
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.85)',
                  marginTop: 4,
                  fontWeight: 600,
                }}
              >
                {l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Vision Card */}
    <div className="vision-card fade-up">
      <div className="vision-star">✦</div>

      <div className="vision-tag">Our Vision</div>

      <p className="vision-text">
        "To set-up a world class manufacturing facility inspired by innovations,
        advanced engineering and smart technologies to design, build and deliver
        successful products and services."
      </p>
    </div>
  </div>

  {/* MOBILE RESPONSIVE */}
  <style jsx>{`
    @media (max-width: 992px) {
      .about-grid {
        grid-template-columns: 1fr !important;
        gap: 28px !important;
      }

      /* IMAGE FIRST IN MOBILE */
      .about-image {
        order: 1;
        height: 340px !important;
      }

      /* CONTENT BELOW IMAGE */
      .about-content {
        order: 2;
      }

      .wrap {
        padding: 0 20px !important;
      }

      .sec-h2 {
        font-size: 34px !important;
        line-height: 1.2;
      }

      .mini-stats {
        display: grid !important;
        grid-template-columns: 1fr 1fr;
        gap: 14px;
      }

      .vision-card {
        padding: 28px 22px !important;
      }

      .vision-text {
        font-size: 15px !important;
        line-height: 1.8;
      }
    }

    @media (max-width: 600px) {
      .about-image {
        height: 280px !important;
        border-radius: 20px !important;
      }

      .sec-h2 {
        font-size: 28px !important;
      }

      .bento-body {
        font-size: 14px !important;
        line-height: 1.9;
      }

      .mini-stats {
        grid-template-columns: 1fr 1fr;
      }

      .mini-stat {
        padding: 14px 10px !important;
      }

      .mini-val {
        font-size: 22px !important;
      }

      .mini-label {
        font-size: 11px !important;
      }

      .vision-card {
        border-radius: 22px !important;
      }
    }
  `}</style>
</section>
      {/* ══ INDUSTRY SHOWCASE ══ */}
      <section className="sec-pad industry-section" style={{ position: 'relative', overflow: 'hidden', padding: '90px 0', background: 'linear-gradient(160deg, #0a1a0e, #0e1f28, #091a14)' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.pexels.com/photos/163170/board-printed-circuit-board-computer-electronics-163170.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.06 }} />

        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 40px', position: 'relative', zIndex: 2 }}>
          <div className="sec-header fade-up" style={{ textAlign: 'center', marginBottom: 40 }}>
            <span className="chip" style={{ background: 'rgba(92,184,92,0.15)', color: '#7ddc7d', border: '1px solid rgba(92,184,92,0.3)' }}>Industries We Serve</span>
            <h2 className="sec-h2" style={{ color: '#e8f5e2' }}>Built for <span style={{ color: '#5cb85c' }}>Every Sector</span></h2>
            <p className="sec-sub" style={{ color: 'rgba(180,220,180,0.7)' }}>From safety-critical defence to high-volume consumer electronics.</p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 30, alignItems: 'flex-start' }}>
            {/* Left tabs */}
            <div style={{ flex: '0 0 300px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              {industrySliders.map((ind, i) => (
                <button
                  key={ind.id}
                  onClick={() => setActiveIndustry(i)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '13px 18px',
                    borderRadius: 12, cursor: 'pointer', textAlign: 'left', width: '100%',
                    border: i === activeIndustry ? `2px solid ${ind.color}` : '2px solid rgba(255,255,255,0.08)',
                    background: i === activeIndustry ? `${ind.color}22` : 'rgba(255,255,255,0.05)',
                    color: i === activeIndustry ? '#fff' : 'rgba(255,255,255,0.6)',
                    fontWeight: i === activeIndustry ? 700 : 500, fontSize: 14,
                    transition: 'all 0.2s',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  <span style={{ fontSize: 18 }}>{ind.icon}</span>
                  <span style={{ flex: 1 }}>{ind.name}</span>
                  {i === activeIndustry && <FaArrowRight size={10} />}
                </button>
              ))}
            </div>

            {/* Right panel */}
            <div style={{ flex: '1 1 600px', minWidth: 0 }}>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontFamily: "'Rajdhani',sans-serif", color: industrySliders[activeIndustry].color, fontSize: '1.8rem', fontWeight: 700, marginBottom: 8 }}>
                  {industrySliders[activeIndustry].name}
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, lineHeight: 1.65 }}>
                  {industrySliders[activeIndustry].desc}
                </p>
              </div>
              <IndustrySlider industry={industrySliders[activeIndustry]} key={activeIndustry} />
              {/* Thumb strip */}
              <div style={{ display: 'flex', gap: 8, marginTop: 12, overflowX: 'auto', paddingBottom: 4 }}>
                {industrySliders[activeIndustry].images.map((img, i) => (
                  <div key={i} style={{ flex: '0 0 auto', width: 74, height: 52, borderRadius: 8, backgroundImage: `url(${img})`, backgroundSize: 'cover', backgroundPosition: 'center', border: '2px solid rgba(255,255,255,0.15)' }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SMT EQUIPMENT ══ */}
      <section className="sec-pad equip-section" style={{ width: '100%' }}>
        <div className="equip-grid-bg" />
        <div className="wrap" style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: '100%', padding: '0 40px' }}>
          <div className="sec-header fade-up">
            <span className="chip">World-Class Equipment</span>
            <h2 className="sec-h2">SMT <span className="green">Production Lines</span></h2>
            <p className="sec-sub">Three full SMT lines from Siemens and Panasonic — built for high-mix, high-volume, high-reliability production.</p>
            <div className="hline" />
          </div>

          {/* Equipment image banner */}
          <div style={{ borderRadius: 20, overflow: 'hidden', height: 240, marginBottom: 48, position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }} className="fade-up">
            <AutoSlider
              images={[
                'https://images.pexels.com/photos/4225118/pexels-photo-4225118.jpeg',
                'https://images.pexels.com/photos/17937673/pexels-photo-17937673.jpeg',
                'https://images.pexels.com/photos/270549/pexels-photo-270549.jpeg',
                'https://images.pexels.com/photos/5118462/pexels-photo-5118462.jpeg',
              ]}
              interval={3500}
            />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4 }}>
              <div style={{ textAlign: 'center', color: '#fff' }}>
                <div style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 32, fontWeight: 700, textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}>World-Class Manufacturing Facility</div>
                <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', marginTop: 8 }}>Mysore, Karnataka · 172,000 Sq.Ft.</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 28, marginBottom: 56 }}>
            <EquipmentCard title="High-Mix / High Volume SMT Line 1" brand="Panasonic" brandColor="#0038a8" brandBg="linear-gradient(135deg,#eff6ff,#dbeafe)" accentColor="#1e5c9e" badge="SMT LINE 1" delay={150} specs={[{ label: 'Model', value: 'CM402-L (KXF-424C)' }, { label: 'PCB Dimensions', value: '50×50 to 510×460 mm' }, { label: 'Max Speed', value: '60,000 CPH' }, { label: 'Chip Accuracy', value: '±0.05 mm/chip' }, { label: 'QFP Accuracy', value: '±0.035 mm' }, { label: 'PCB Exchange', value: '0.9s (up to 240 mm)' }]} />
            <EquipmentCard title="NEW High-Mix / High Volume SMT Line 2" brand="Panasonic" brandColor="#0038a8" brandBg="linear-gradient(135deg,#faf5ff,#ede9fe)" accentColor="#7c3d8a" badge="NEW LINE 2" delay={300} specs={[{ label: 'Model', value: 'NPM-D3A' }, { label: 'PCB (Dual-lane)', value: 'L50–510 × W300 mm' }, { label: 'Max Speed', value: ' 1,40,000 CPH' }, { label: 'Placement Acc.', value: '±37 µm / chip' }, { label: 'Electric Source', value: '3-phase AC 200–480V' }, { label: 'Traceability', value: 'PanaCIM Software' }]} />
          </div>

          <div className="sec-header fade-up" style={{ marginTop: 20 }}>
            <span className="chip">Precision Inspection</span>
            <h2 className="sec-h2">Quality <span className="green">Assurance Systems</span></h2>
            <p className="sec-sub">Cyberoptics SPI & AOI plus BGA rework — ensuring zero-defect assembly at every stage.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 28 }}>
            <EquipmentCard title="Precision BGA Fully Automatic Rework Station" brand="ZHUOMAO" brandColor="#c0392b" brandBg="linear-gradient(135deg,#fff5f5,#fecaca)" accentColor="#c2410c" badge="BGA STATION" delay={0} specs={[{ label: 'PCB Size', value: '670×570mm Max, 10×10mm Min' }, { label: 'BGA Chip', value: '100×100mm Max, 1×1mm Min' }, { label: 'Total Power', value: '21.25 KW' }, { label: 'IR Temp Zone', value: '645×524 mm' }, { label: 'Alignment', value: '1.3M pixel HD industrial camera' }, { label: 'Motion Control', value: 'X/Y/Z Servo system' }]} />
            <EquipmentCard title="Solder Paste Inspection System" brand="Cyberoptics" brandColor="#b45309" brandBg="linear-gradient(135deg,#fffbeb,#fef3c7)" accentColor="#b45309" badge="SPI" delay={150} specs={[{ label: 'Panel Capacity', value: '50×50 to 510×510 mm' }, { label: 'Board Thickness', value: '0.3 to 5.0 mm' }, { label: 'Paste Height', value: '50–500 µm (2–20 mils)' }, { label: 'Height Resolution', value: '0.2 µm (0.008 mils)' }, { label: 'Conveyor Speed', value: '150–450 mm/sec' }, { label: 'Interface', value: 'SMEMA, RS232 & Ethernet' }]} />
            <EquipmentCard title="QX600 Automated Optical Inspection" brand="Cyberoptics" brandColor="#e67e22" brandBg="linear-gradient(135deg,#f0fdf4,#dcfce7)" accentColor="#2e7d32" badge="AOI" delay={300} specs={[{ label: 'Scanning Speed', value: '200 cm²/sec' }, { label: 'Min Component', value: '0402 mm (01005 in.)' }, { label: 'Board Length', value: 'Max 510 mm (QX600-L)' }, { label: 'Vision Tech', value: 'SAM + AI² Autonomous' }, { label: 'Defect Types', value: 'Missing, polarity, BGA, tombstone' }, { label: 'Height Clearance', value: '35 mm max' }]} />
          </div>
        </div>
      </section>

      {/* ══ KEY PROJECTS ══ */}
     <section className="sec-pad" style={{ 
  background: 'linear-gradient(160deg,#fafffe,#f0fdf4,#fffbf0)', 
  position: 'relative', 
  overflow: 'hidden' 
}}>
  {/* Background image strip */}
  <div style={{ 
    position: 'absolute', 
    top: 0, 
    left: 0, 
    right: 0, 
    height: 6, 
    background: 'linear-gradient(90deg, #5cb85c, #0d6e6e, #5cb85c, #1e5c9e, #5cb85c)', 
    backgroundSize: '400% auto', 
    animation: 'shimmer 6s linear infinite' 
  }} />

  <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 40px' }}>
    <div className="sec-header fade-up">
      <span className="chip">Proven Delivery</span>
      <h2 className="sec-h2">Key <span className="green">Projects</span></h2>
      <p className="sec-sub">Real-world results across global OEMs — tracked, measured, delivered.</p>
      <div className="hline" />
    </div>

    <div className="proj-grid fade-up">
      {keyProjects.map((p, i) => (
        <div key={i} className="proj-card" style={{ '--sc': p.color, '--sbg': p.bg }}>
          <div className="proj-card-topbar" style={{ background: p.color }} />
          
          <div style={{ padding: '16px 20px 18px' }}>
            <span className="proj-sector" style={{ 
              color: p.color, 
              background: `${p.color}15`, 
              border: `1px solid ${p.color}35` 
            }}>
              {p.sector}
            </span>
            
            <div className="proj-company">{p.company}</div>
            <div className="proj-scope">{p.scope}</div>

            {/* Updated Metrics - Left & Right Layout */}
            <div className="proj-metrics" style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '24px',
              marginTop: '20px'
            }}>
              {/* Left Side - PCBA Produced */}
              <div className="pm" style={{ textAlign: 'left' }}>
                <div className="pm-val" style={{ 
                  color: p.color, 
                  fontSize: '1.65rem', 
                  fontWeight: '700',
                  lineHeight: '1.1'
                }}>
                  {p.metric}
                </div>
                <div className="pm-lbl" style={{ 
                  color: '#555', 
                  fontSize: '0.9rem', 
                  marginTop: '4px' 
                }}>
                  {p.metricLabel}
                </div>
              </div>

              {/* Right Side - On-Time Delivery */}
              <div className="pm" style={{ textAlign: 'right' }}>
                <div className="pm-val" style={{ 
                  color: '#2e7d32', 
                  fontSize: '1.65rem', 
                  fontWeight: '700',
                  lineHeight: '1.1'
                }}>
                  {p.otd}
                </div>
                <div className="pm-lbl" style={{ 
                  color: '#555', 
                  fontSize: '0.9rem', 
                  marginTop: '4px' 
                }}>
                  On-Time Delivery
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>

    <div style={{ textAlign: 'center', marginTop: 40 }}>
      <Link to="/projects" className="btn-glow">
        View All Projects <FaArrowRight size={12} />
      </Link>
    </div>
  </div>
</section>
      {/* ══ WHY US — with images ══ */}
      <section className="sec-pad" style={{ background: '#fff', position: 'relative', overflow: 'hidden' }}>
        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 40px' }}>
          <div className="sec-header fade-up">
            <span className="chip">Why Saalfo Tech</span>
            <h2 className="sec-h2">Your Trusted <span className="green">EMS Partner</span></h2>
            <div className="hline" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }} className="fade-up">
            {whyUs.map((w, i) => (
              <div key={w.title} className="why-card-img" style={{ '--wc': w.color }}>
                {/* Image top */}
                <div style={{ height: 160, overflow: 'hidden', borderRadius: '16px 16px 0 0', position: 'relative' }}>
                  <img src={w.img} alt={w.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }} className="why-img-zoom" />
                  <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${w.color}cc 0%, transparent 60%)` }} />
                  <div style={{ position: 'absolute', top: 14, left: 14, width: 42, height: 42, borderRadius: 11, background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 17 }}>
                    {w.icon}
                  </div>
                </div>
                {/* Text */}
                <div style={{ padding: '18px 20px 22px', background: '#fff', borderRadius: '0 0 16px 16px', border: `1.5px solid ${w.color}22`, borderTop: 'none', flex: 1 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: w.color, marginBottom: 9 }}>{w.title}</h3>
                  <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65 }}>{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CERTIFICATIONS ══ */}
     <section className="sec-pad-sm cert-section" style={{ width: '100%', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.pexels.com/photos/5474295/pexels-photo-5474295.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.06 }} />
        <div className="cert-mesh" />
        <div className="wrap" style={{ position: 'relative', zIndex: 2, width: '100%', maxWidth: '100%', padding: '0 40px' }}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <span className="chip chip-light">Compliance</span>
            <h3 className="sec-h2 sec-h2-light" style={{ marginTop: 12 }}>Certifications & <span style={{ color: '#7ddc7d' }}>Standards</span></h3>
            <p style={{ color: 'rgba(180,230,180,0.7)', fontSize: 14, marginTop: 8 }}>Current and targeted certifications for 2026-27</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>

            {/* Membership Certificate — featured first, opens local PDF */}
            <a
              href={membershipPdf}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                cursor: 'pointer',
                display: 'block',
                position: 'relative',
                borderRadius: 16,
                padding: '22px 18px',
                textAlign: 'center',
                background: 'linear-gradient(145deg, rgba(92,184,92,0.18), rgba(13,110,110,0.12))',
                border: '1.5px solid rgba(125,220,125,0.5)',
                boxShadow: '0 8px 30px rgba(92,184,92,0.25)',
                transition: 'all 0.25s',
              }}
            >
              {/* Ribbon */}
              <span style={{
                position: 'absolute',
                top: 0,
                right: 0,
                background: 'linear-gradient(135deg, #5cb85c, #0d6e6e)',
                color: '#fff',
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: 0.5,
                padding: '4px 10px',
                borderRadius: '0 16px 0 10px',
                textTransform: 'uppercase',
              }}>
                Startup
              </span>

              <FaCertificate size={28} style={{ color: '#7ddc7d', marginBottom: 10 }} />
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 5 }}>Membership Certificate</div>
              <div style={{ fontSize: 11, color: 'rgba(180,220,180,0.7)', marginBottom: 12 }}> Recognition (A-269)</div>
              <span style={{
                padding: '3px 10px',
                borderRadius: 100,
                fontSize: 10,
                fontWeight: 700,
                background: 'rgba(92,184,92,0.2)',
                color: '#7ddc7d',
              }}>
                View Certificate
              </span>
            </a>

            {[
              { name: 'ISO 9001:2015', body: 'TUV SUD', status: 'Active', color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
              { name: 'ZED Bronze', body: 'Ministry of MSME', status: 'Active', color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
              { name: 'IPC Class II & III', body: 'IPC Certified Facility', status: 'Active', color: '#2e7d32', bg: 'rgba(46,125,50,0.12)' },
              { name: 'ISO 14001', body: 'Environmental', status: '2026-27', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
              { name: 'ISO 45001', body: 'Safety', status: '2026-27', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
              { name: 'ISO 13485', body: 'Medical Devices', status: '2026-27', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
              { name: 'AS 9100', body: 'Aerospace', status: '2026-27', color: '#94a3b8', bg: 'rgba(148,163,184,0.1)' },
            ].map(c => (
              <div key={c.name} className="crt-card" style={{ '--cc': c.color, '--cbg': c.bg }}>
                <FaCertificate size={20} style={{ color: c.color, marginBottom: 8 }} />
                <div className="crt-name">{c.name}</div>
                <div className="crt-body">{c.body}</div>
                <span className={`crt-badge ${c.status === 'Active' ? 'act' : 'up'}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA ══ */}
      <section className="cta-sec" style={{ width: '100%', position: 'relative', overflow: 'hidden', padding: '100px 0' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(https://images.pexels.com/photos/3862130/pexels-photo-3862130.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
        <div className="cta-orb cta-orb-1" />
        <div className="cta-orb cta-orb-2" />
        <div className="cta-orb cta-orb-3" />
        <div className="wrap" style={{ width: '100%', maxWidth: '100%', padding: '0 40px', textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div className="cta-leaf-icon"><FaLeaf size={26} /></div>
          <h2 className="cta-h2">Let's build something great together</h2>
          <p className="cta-sub">Share your requirements — our team responds within 24 hours.</p>
          <div className="cta-btns">
            <Link to="/contact" className="btn-glow">Contact Us <FaArrowRight size={12} /></Link>
            <Link to="/projects" className="btn-ghost-dark">View Projects</Link>
          </div>
          <div className="cta-addr">
            <FaMapMarkerAlt size={11} style={{ color: '#5cb85c' }} />
            Mysore, Karnataka, India
          </div>
        </div>
      </section>
    </div>
  );
}

const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Rajdhani:wght@600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  .wrap{max-width:1200px;margin:0 auto;padding:0 24px}
  .sec-pad{padding:90px 0}
  .sec-pad-sm{padding:60px 0}
  .green{color:#5cb85c}

  @keyframes fadeUp{from{opacity:0;transform:translateY(28px)}to{opacity:1;transform:translateY(0)}}
  .fade-up{animation:fadeUp 0.65s ease both}
  @keyframes pulse{0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.1);opacity:0.7}}
  @keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}
  @keyframes rotateSlow{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  @keyframes pulseDot{0%,100%{transform:scale(1);opacity:0.6}50%{transform:scale(1.5);opacity:1}}
  @keyframes shimmer{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}

  .chip{display:inline-block;padding:5px 16px;background:#eaf6ea;color:#2d6a2d;font-size:11px;font-weight:800;border-radius:100px;letter-spacing:.8px;margin-bottom:14px;border:1px solid #c3e6c3;text-transform:uppercase}
  .chip-light{background:rgba(92,184,92,0.15);color:#7ddc7d;border-color:rgba(92,184,92,0.3)}
  .sec-header{text-align:center;margin-bottom:52px}
  .sec-h2{font-family:'Rajdhani',sans-serif;font-size:clamp(26px,5vw,44px);font-weight:700;color:#1a1a2e;margin-bottom:12px}
  .sec-h2-light{color:#e8f5e2}
  .sec-sub{font-size:15px;color:#64748b;max-width:540px;margin:0 auto;line-height:1.7}
  .hline{width:54px;height:4px;background:linear-gradient(90deg,#5cb85c,#3d8b3d);border-radius:4px;margin:14px auto 0}

  /* HERO */
  .hero-section{position:relative;overflow:hidden;min-height:100vh;display:flex;align-items:center}
  .hero-grid{display:flex;align-items:center;position:relative;gap:60px;padding-bottom:60px}
  .hero-left{position:relative;z-index:1;flex:1}
  .hero-right{position:relative;z-index:1;flex:1;max-width:480px}

  .hero-badge{display:inline-flex;align-items:center;gap:6px;padding:6px 14px;border-radius:100px;font-size:12px;font-weight:600;margin-bottom:20px}
  .hero-h1{font-family:'Rajdhani',sans-serif;font-size:clamp(34px,5.5vw,62px);font-weight:700;color:#fff;line-height:1.1;margin-bottom:22px}
  .glow-text{color:#5cb85c;text-shadow:0 0 40px rgba(92,184,92,.6),0 0 80px rgba(92,184,92,.3)}
  .hero-sub{font-size:17px;line-height:1.75;margin-bottom:32px;max-width:480px}
  .hero-btns{display:flex;gap:12px;flex-wrap:wrap;margin-bottom:24px}
  .btn-glow{display:inline-flex;align-items:center;gap:8px;padding:14px 30px;background:linear-gradient(135deg,#5cb85c,#3d8b3d);color:#fff;font-weight:700;font-size:14px;border-radius:12px;text-decoration:none;transition:all .25s;box-shadow:0 4px 24px rgba(92,184,92,.45)}
  .btn-glow:hover{transform:translateY(-2px);box-shadow:0 10px 36px rgba(92,184,92,.6)}
  .btn-ghost{display:inline-flex;align-items:center;gap:8px;padding:14px 28px;font-weight:700;font-size:14px;border-radius:12px;text-decoration:none;transition:all .25s}
  .btn-ghost:hover{transform:translateY(-2px)}
  .cert-pills{display:flex;flex-wrap:wrap;gap:8px}
  .cert-pill{padding:5px 12px;border-radius:100px;font-size:11px;font-weight:700}

  .hero-card{position:relative;border-radius:24px;padding:36px;overflow:hidden;transition:box-shadow .3s,transform .3s}
  .hero-card:hover{transform:translateY(-4px)}
  .hero-card-glow{position:absolute;top:-60px;right:-60px;width:200px;height:200px;background:radial-gradient(circle,rgba(92,184,92,.15),transparent 70%);pointer-events:none}
  .hero-card-accent{position:absolute;bottom:0;left:0;width:100%;height:3px;background:linear-gradient(90deg,#5cb85c,#0d6e6e,#5cb85c);background-size:200% auto;animation:shimmer 3s linear infinite}
  .hero-logo{height:48px;margin-bottom:10px}
  .hero-card-tag{font-family:'Rajdhani',sans-serif;font-size:12px;font-weight:700;margin-bottom:20px;letter-spacing:1.2px}
  .stat-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:18px}
  .stat-box{border-radius:12px;padding:14px 10px;text-align:center;transition:transform .2s,box-shadow .2s;cursor:default}
  .stat-box:hover{transform:scale(1.04) translateY(-2px)}
  .stat-num{font-family:'Rajdhani',sans-serif;font-size:28px;font-weight:700;line-height:1}
  .stat-label{font-size:11px;margin-top:4px;color:rgba(255,255,255,0.6)}
  .hero-pills{display:flex;gap:8px;flex-wrap:wrap}
  .hero-pill{padding:5px 12px;border-radius:100px;font-size:11px;font-weight:600}

  /* MARQUEE */
  .marquee-wrap{background:linear-gradient(135deg,#1a2e1a,#0f1f2e,#1a1a2e);padding:13px 0;overflow:hidden;position:relative;z-index:5}
  .marquee-track{display:flex;animation:marquee 30s linear infinite;white-space:nowrap}
  .marquee-item{display:inline-flex;align-items:center;gap:7px;padding:0 28px;font-size:12px;font-weight:600;color:rgba(255,255,255,.7)}

  /* IMAGE SERVICE CARDS */
  .img-svc-card{border-radius:18px;overflow:hidden;box-shadow:0 8px 32px rgba(0,0,0,0.09);transition:all 0.35s;display:flex;flex-direction:column}
  .img-svc-card:hover{transform:translateY(-8px);box-shadow:0 24px 60px rgba(0,0,0,0.16)}
  .img-svc-card:hover .why-img-zoom{transform:scale(1.05)}

  /* ABOUT */
  .about-section{position:relative;overflow:hidden;padding:90px 0}

  .bento-tags{display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px}
  .btag{padding:5px 13px;border-radius:100px;font-size:11px;font-weight:800;border:1px solid transparent}
  .btag-green{background:#dcfce7;color:#15803d;border-color:#bbf7d0}
  .btag-teal{background:#ccfbf1;color:#0f766e;border-color:#99f6e4}
  .btag-amber{background:#fef3c7;color:#92400e;border-color:#fde68a}
  .bento-body{font-size:15px;line-height:1.72;color:#374151}
  .mini-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-top:22px}
  .mini-stat{background:var(--mbg,#f0fdf4);border-radius:12px;padding:12px 6px;text-align:center;border:1px solid color-mix(in srgb,var(--mc,#2e7d32) 22%,white)}
  .mini-val{font-family:'Rajdhani',sans-serif;font-size:22px;font-weight:700;color:var(--mc,#2e7d32)}
  .mini-label{font-size:10px;color:#64748b;margin-top:3px}
  .vision-card{background:linear-gradient(145deg,#1a2e3e,#162a20,#1a2a3a);border-radius:20px;padding:36px;color:#fff;text-align:center}
  .vision-star{font-size:26px;color:#5cb85c;margin-bottom:10px;display:block;animation:float 3s ease-in-out infinite}
  .vision-tag{font-size:11px;font-weight:700;color:#7ddc7d;letter-spacing:2px;margin-bottom:12px;text-transform:uppercase}
  .vision-text{font-size:15px;color:rgba(255,255,255,.85);line-height:1.8;font-style:italic;max-width:700px;margin:0 auto}

  /* STATS */
  .stats-section{position:relative;background:linear-gradient(135deg,#2e7d32,#4caf50,#0d6e6e,#2e7d32);background-size:300% 300%;animation:shimmer 8s ease infinite;padding:70px 0;overflow:hidden}
  .stats-shimmer{position:absolute;inset:0;background:radial-gradient(ellipse at center,rgba(255,255,255,.07),transparent 70%)}
  .stats-row{display:grid;grid-template-columns:repeat(4,1fr);gap:20px}
  .perf-card{padding:28px;border-radius:18px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.22);text-align:center;transition:all .25s;backdrop-filter:blur(4px)}
  .perf-card:hover{background:rgba(255,255,255,.22);transform:translateY(-4px)}
  .perf-icon{font-size:26px;margin-bottom:10px}
  .perf-val{font-family:'Rajdhani',sans-serif;font-size:40px;font-weight:700;color:#fff;line-height:1}
  .perf-label{font-size:13px;color:rgba(255,255,255,.9);font-weight:600;margin:6px 0 3px}
  .perf-sub{font-size:11px;color:rgba(255,255,255,.65)}

  /* INDUSTRY */
  .industry-section{position:relative;overflow:hidden;padding:90px 0}

  /* EQUIPMENT */
  .equip-section{background:linear-gradient(160deg,#fff,#f0fdf4,#fffbf0,#fff);position:relative;overflow:hidden}
  .equip-grid-bg{position:absolute;inset:0;background-image:radial-gradient(circle,rgba(92,184,92,.03) 1px,transparent 1px);background-size:32px 32px;pointer-events:none}
  .equip-card{position:relative;border-radius:20px;overflow:hidden;border:1.5px solid #e8efe8;transition:all .3s;box-shadow:0 4px 20px rgba(0,0,0,.05)}
  .equip-card:hover{transform:translateY(-6px);box-shadow:0 18px 50px rgba(0,0,0,.10);border-color:var(--ac,#5cb85c)}
  .equip-card:hover .equip-glow{opacity:1}
  .equip-glow{position:absolute;top:0;right:0;width:140px;height:140px;opacity:0;transition:opacity .3s;pointer-events:none}
  .equip-card-header{padding:18px 20px 14px;position:relative}
  .equip-badge-top{display:inline-block;padding:3px 11px;color:#fff;font-size:10px;font-weight:800;border-radius:100px;letter-spacing:.8px;margin-bottom:8px}
  .equip-brand{font-family:'Rajdhani',sans-serif;font-size:24px;font-weight:700}
  .equip-body{padding:0 20px 20px;background:#fff}
  .equip-title{font-size:13px;font-weight:600;color:#2d3748;line-height:1.4;margin-bottom:14px;padding-bottom:12px;border-bottom:1px solid #f0f4f0}
  .equip-specs{display:flex;flex-direction:column;gap:6px}
  .equip-spec-row{display:flex;justify-content:space-between;align-items:flex-start;gap:8px;padding:5px 8px;border-radius:6px}
  .equip-spec-label{font-size:11px;color:#94a3b8;font-weight:600;flex-shrink:0;max-width:48%}
  .equip-spec-val{font-size:11px;font-weight:700;text-align:right}

  /* PROJECTS */
  .proj-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
  .proj-card{position:relative;background:#fff;border-radius:18px;overflow:hidden;border:1.5px solid #e8efe8;transition:all .3s}
  .proj-card:hover{transform:translateY(-5px);box-shadow:0 14px 40px rgba(0,0,0,.08);border-color:var(--sc)}
  .proj-card-topbar{height:5px;width:100%}
  .proj-sector{display:inline-block;padding:4px 12px;border-radius:100px;font-size:10px;font-weight:800;margin-bottom:10px;letter-spacing:.5px}
  .proj-company{font-size:15px;font-weight:700;color:#1a1a2e;margin-bottom:5px}
  .proj-scope{font-size:12px;color:#64748b;line-height:1.5;margin-bottom:14px;padding-bottom:14px;border-bottom:1px solid #f1f5f9}
  .proj-metrics{display:grid;grid-template-columns:repeat(3,1fr);gap:8px}
  .pm{text-align:center}
  .pm-val{font-family:'Rajdhani',sans-serif;font-size:17px;font-weight:700}
  .pm-lbl{font-size:9px;color:#94a3b8;margin-top:2px}

  /* WHY US — IMAGE CARDS */
  .why-card-img{border-radius:16px;overflow:hidden;box-shadow:0 6px 24px rgba(0,0,0,0.07);transition:all 0.3s;display:flex;flex-direction:column;border:1.5px solid transparent}
  .why-card-img:hover{transform:translateY(-6px);box-shadow:0 20px 50px rgba(0,0,0,0.12);border-color:color-mix(in srgb,var(--wc,#2e7d32) 30%,white)}
  .why-card-img:hover .why-img-zoom{transform:scale(1.08)}
  .why-img-zoom{transition:transform 0.5s ease}

  /* CERTS */
  .cert-section{background:linear-gradient(160deg,#0f2010,#0f1a2e,#162820);position:relative;overflow:hidden}
  .cert-mesh{position:absolute;inset:0;background-image:radial-gradient(circle at 30% 50%,rgba(92,184,92,.07) 0%,transparent 50%),radial-gradient(circle at 70% 30%,rgba(13,110,110,.06) 0%,transparent 45%);pointer-events:none}
  .crt-card{background:var(--cbg,rgba(92,184,92,.08));border:1px solid color-mix(in srgb,var(--cc,#5cb85c) 30%,transparent);border-radius:16px;padding:22px 18px;text-align:center;transition:all .25s}
  .crt-card:hover{transform:translateY(-4px);box-shadow:0 10px 28px rgba(0,0,0,.25)}
  .crt-name{font-size:14px;font-weight:700;color:#fff;margin-bottom:5px}
  .crt-body{font-size:11px;color:rgba(180,220,180,.7);margin-bottom:12px}
  .crt-badge{padding:3px 10px;border-radius:100px;font-size:10px;font-weight:700}
  .crt-badge.act{background:rgba(92,184,92,.2);color:#7ddc7d}
  .crt-badge.up{background:rgba(148,163,184,.15);color:#94a3b8}

  /* CTA */
  .cta-sec{position:relative;background:linear-gradient(145deg,#0f2010,#0a1a2e,#152010);overflow:hidden}
  .cta-orb{position:absolute;border-radius:50%;pointer-events:none}
  .cta-orb-1{width:420px;height:420px;top:-120px;left:-100px;background:radial-gradient(circle,rgba(92,184,92,.12),transparent 70%);animation:pulse 7s ease-in-out infinite}
  .cta-orb-2{width:360px;height:360px;bottom:-120px;right:-80px;background:radial-gradient(circle,rgba(13,110,110,.10),transparent 70%);animation:pulse 9s ease-in-out infinite reverse}
  .cta-orb-3{width:200px;height:200px;top:40%;left:42%;background:radial-gradient(circle,rgba(92,184,92,.06),transparent 70%);animation:pulse 5s ease-in-out infinite}
  .cta-leaf-icon{width:62px;height:62px;background:rgba(92,184,92,.15);border-radius:50%;display:flex;align-items:center;justify-content:center;margin:0 auto 18px;color:#5cb85c;border:1px solid rgba(92,184,92,.3);animation:float 3s ease-in-out infinite}
  .cta-h2{font-family:'Rajdhani',sans-serif;font-size:clamp(24px,5vw,42px);font-weight:700;color:#fff;margin-bottom:14px}
  .cta-sub{font-size:16px;color:rgba(255,255,255,.6);margin-bottom:36px}
  .cta-btns{display:flex;gap:12px;justify-content:center;flex-wrap:wrap;margin-bottom:24px}
  .btn-ghost-dark{display:inline-flex;align-items:center;gap:8px;padding:14px 26px;background:rgba(255,255,255,.08);color:rgba(255,255,255,.85);font-weight:700;font-size:14px;border-radius:12px;text-decoration:none;border:1px solid rgba(255,255,255,.15);transition:all .25s}
  .btn-ghost-dark:hover{background:rgba(255,255,255,.14);transform:translateY(-2px)}
  .cta-addr{font-size:13px;color:rgba(255,255,255,.4);display:flex;align-items:center;gap:8px;justify-content:center}

  /* RESPONSIVE */
  @media(max-width:1024px){
    .hero-grid{flex-direction:column;gap:40px;padding-top:40px}
    .hero-right{max-width:100%}
    .stats-row{grid-template-columns:repeat(2,1fr)}
    .proj-grid{grid-template-columns:repeat(2,1fr)}
    .mini-stats{grid-template-columns:repeat(4,1fr)}
  }
  @media(max-width:768px){
    .hero-section{min-height:auto;padding-bottom:40px}
    .sec-pad{padding:60px 0}
    .sec-pad-sm{padding:48px 0}
    .proj-grid{grid-template-columns:1fr}
    .stats-row{grid-template-columns:1fr 1fr}
    .mini-stats{grid-template-columns:repeat(2,1fr)}
  }
  @media(max-width:520px){
    .hero-btns{flex-direction:column}
    .btn-glow,.btn-ghost{width:100%;justify-content:center}
    .stat-grid{grid-template-columns:1fr 1fr}
    .cta-btns{flex-direction:column;align-items:center}
    .btn-glow,.btn-ghost-dark{width:100%;justify-content:center}
  }
`;
